import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AlertsService, CLToastType } from '@clavisco/alerts';
import { Observable } from 'rxjs';
import { AuthenticationService } from '../services/authentication.service';
import { DataStorageService } from '../services/data-storage.service';
import { GlobalService } from '../services/global.service';
import { IPermission } from '../interfaces/i-permission';

/**
 * PermissionGuard is an Angular service that implements the CanActivate interface.
 * It is used to protect routes by checking if the user has the required permissions.
 * If the user lacks the necessary permissions, they are redirected to a specific page and shown an error message.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate {

  /**
   * Constructor for PermissionGuard.
   * @param authService - Service to handle authentication logic.
   * @param router - Angular Router for navigation.
   * @param alertService - Service to display alerts and notifications.
   * @param dataStorageService - Service to manage data storage.
   * @param globalService - Service to handle global application logic.
   */
  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private alertService: AlertsService,
    private dataStorageService: DataStorageService,
    private globalService: GlobalService
  ) {}

  /**
   * Determines whether a route can be activated based on the user's permissions.
   * @param route - The activated route snapshot.
   * @param state - The router state snapshot.
   * @returns A boolean, UrlTree, Observable, or Promise indicating whether the route can be activated.
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const actualPath: string = this.globalService.getRoutePath(route);
    const perms: IPermission[] = this.dataStorageService.GetItem<IPermission[]>('permissions');
    if (!perms) {
      this.HandledUnauthorizedAccess('/login');
    }
    const reqPerm: string = this.globalService.GetPermissionsByPath(actualPath);
    const hasPerm = this.globalService.ValidatePermission(perms, reqPerm);
    if (!hasPerm) {
      this.HandledUnauthorizedAccess('/home');
    }

    return true;
  }

  /**
   * Handles unauthorized access by displaying an error toast and redirecting the user.
   * @param redirectPath - The path to which the user will be redirected.
   * @returns A boolean indicating that the route cannot be activated.
   */
  private HandledUnauthorizedAccess(redirectPath: string): boolean {
    this.alertService.Toast({ message: 'No tiene permisos para acceder a esta página', type: CLToastType.ERROR });
    const urlTree: UrlTree = this.router.createUrlTree([redirectPath], {});
    this.router.navigateByUrl(urlTree);
    return false;
  }
}
